import { ZucchettiCompensationsExportFeatureDocument } from 'generated/resources/graphql'

import { useAsyncData } from 'modules/core/hooks/useAsyncData'
import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useQuery } from 'modules/core/hooks/useQuery'
import i18n from 'modules/core/lib/i18n'
import legalEntities from 'modules/core/store/collections/legalEntities'

import { ResponsivePage } from 'modules/layout/components/ResponsivePage'
import { useGetMarketplaceCompanyInstallations } from 'modules/marketplace/hooks/integrations/companyInstallations/useGetMarketplaceCompanyInstallations'
import integrationCodes from 'modules/marketplace/lib/integrationCodes'
import { useConcepts } from 'modules/payroll_bundle/hooks/useConcepts'
import { useHasCompensations } from 'modules/payroll_bundle/hooks/useHasCompensations'
import { buildPayrollConceptsByLegalEntitySettings } from 'modules/payroll_bundle/lib/settingsPayrollConcept'
import PayrollPaywallGuard from 'modules/payroll_bundle/pages/PayrollPaywallGuard'
import ArchivedSection from 'modules/payroll_bundle/pages/settings/ZucchettiIntegration/ArchivedSection'
import { useSplitTimeSettingsCustomTimeRangeCategories } from 'modules/payroll_bundle/pages/settings/ZucchettiIntegration/split_custom_time_range_categories/useSplitTimeSettingsCustomTimeRangeCategories'
import EmployeesData from 'modules/payroll_integrations/components/Content/EmployeeData'
import Table from 'modules/payroll_integrations/components/Table'
import PayrollIntegration from 'modules/payroll_integrations/lib/catalog'
import type { Codeable, ColumnNames } from 'modules/payroll_integrations/lib/types'
import leaveTypes from 'modules/timeoff/store/collections/leaveTypes'

const ZucchettiIntegration = () => {
  const { company } = useCurrentSession()

  const INTEGRATION_NAME = PayrollIntegration.zucchetti

  const COLUMN_NAMES: ColumnNames = [
    {
      name: i18n.t(
        'app_store_v2.app_integrations.payroll_integrations_zucchetti.settings.form.header.concept'
      ),
      colSpan: 4,
      attribute: 'concept',
    },
    {
      name: i18n.t(
        'app_store_v2.app_integrations.payroll_integrations_zucchetti.settings.form.header.value'
      ),
      colSpan: 4,
      attribute: 'value',
    },
  ]

  const { hasCompensations, loading: loadingHasCompensations } = useHasCompensations()

  const { data: featureFlagData, loading: loadingFeatureFlag } = useQuery(
    ZucchettiCompensationsExportFeatureDocument,
    {
      variables: { companyId: company.id },
    }
  )

  const hasZucchettiCompensationsExport =
    featureFlagData?.features?.featuresConnection?.nodes?.[0]?.enabled ?? false

  const { concepts: payrollConcepts, loading: loadingConcepts } = useConcepts(
    undefined,
    [
      'deductions',
      'earnings_benefits_in_kind',
      'earnings_fixed_salary',
      'earnings_others',
      'earnings_variable',
    ],
    true,
    !hasCompensations
  )

  const { data: companyInstallationsData, loading: companyInstallationsDataLoading } =
    useGetMarketplaceCompanyInstallations(
      {
        integrationDatoIds: [integrationCodes.zucchetti],
      },
      !hasCompensations
    )

  const zucchettiInstallation = companyInstallationsData[0]

  const payrollConceptsByLegalEntity = buildPayrollConceptsByLegalEntitySettings(
    payrollConcepts,
    legalEntities
  )

  const { splitCustomTimeRangeCategories, loading: loadingSplitCategories } =
    useSplitTimeSettingsCustomTimeRangeCategories(company.id)

  const { loading: loadingCollections } = useAsyncData(() => {
    return Promise.all([leaveTypes.fetch()])
  }, [])

  const codeables: Record<string, Codeable> = {
    legalEntity: {
      position: 0,
      collection: legalEntities,
      class: 'LegalEntity',
      label: i18n.t(
        'app_store_v2.app_integrations.payroll_integrations_zucchetti.settings.form.concept.legal_entity'
      ),
    },
    leave_type: {
      position: 1,
      collection: leaveTypes.filter((lt) => lt.isActive),
      class: 'Timeoff::LeaveType',
      label: i18n.t(
        'app_store_v2.app_integrations.payroll_integrations_zucchetti.settings.form.concept.leave_type'
      ),
    },
    splitCustomTimeRangeCategory: {
      position: 2,
      collection: splitCustomTimeRangeCategories,
      class: 'TimeSettings::SplitCustomTimeRangeCategory',
      label: i18n.t(
        'app_store_v2.app_integrations.payroll_integrations_zucchetti.settings.form.concept.custom_time_category'
      ),
    },
  }

  const archivedLeaveTypes = leaveTypes.filter((lt) => !lt.isActive)

  const archivedCodeables: Record<string, Codeable> = {
    leave_type: {
      position: 0,
      collection: archivedLeaveTypes,
      class: 'Timeoff::LeaveType',
      label: i18n.t(
        'app_store_v2.app_integrations.payroll_integrations_zucchetti.settings.form.concept.leave_type'
      ),
    },
  }

  if (hasCompensations && hasZucchettiCompensationsExport) {
    codeables.payrollConcepts = {
      position: 3,
      collection: payrollConceptsByLegalEntity || [],
      class: 'Payroll::Concept',
      label: i18n.t(
        'app_store_v2.app_integrations.payroll_integrations_a3nom.settings.form.payroll_concept'
      ),
    }
  }

  return (
    <PayrollPaywallGuard>
      <ResponsivePage.Body
        variant="full"
        loading={
          loadingHasCompensations ||
          loadingFeatureFlag ||
          loadingCollections ||
          loadingConcepts ||
          companyInstallationsDataLoading ||
          loadingSplitCategories
        }
        error={null}
      >
        <Table
          columnNames={COLUMN_NAMES}
          codeables={codeables}
          integration={INTEGRATION_NAME}
          integrationInstallation={zucchettiInstallation}
        />
        {archivedLeaveTypes.length > 0 && (
          <ArchivedSection
            columnNames={COLUMN_NAMES}
            codeables={archivedCodeables}
            integration={INTEGRATION_NAME}
            integrationInstallation={zucchettiInstallation}
          />
        )}
        <EmployeesData
          integration_name={i18n.t(
            'app_store_v2.app_integrations.payroll_integrations_zucchetti.integration_name'
          )}
        />
      </ResponsivePage.Body>
    </PayrollPaywallGuard>
  )
}

export default ZucchettiIntegration
